import { useState, useEffect } from 'react';
import { Button, Modal, IconButton, HeaderGlobalAction } from '@carbon/react';
import { Reset } from '@carbon/icons-react';
import { useFetcher } from 'react-router';
import { Img } from 'openimg/react';
import { Icon } from '#app/components/ui/icon.tsx';
import { getUserImgSrc } from '#app/utils/misc.tsx';

interface TeamMember {
  id: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
  imageObjectKey: string | null;
  employmentRecord: {
    employmentType: string;
    position: {
      title: string;
    } | null;
    department: {
      name: string;
    } | null;
    secondaryPositions: {
      position: {
        title: string;
      };
      department: {
        name: string;
      };
    }[];
  } | null;
  status: 'working' | 'break' | 'offline';
  sessionStart: string | null;
  totalToday: string | null;
  workSeconds: number;
  breakInfo: string | null;
  lastSeen: string | null;
  isSelf: boolean;
  isOnLeave: boolean;
  leaveTypeName: string | null;
}

interface VirtualOfficeData {
  teamStatus: TeamMember[];
  userTimezone: string;
}

export function VirtualOfficeButton() {
  const [isOpen, setIsOpen] = useState(false);
  const fetcher = useFetcher<VirtualOfficeData>();

  // Fetch data on mount to show correct status on button
  useEffect(() => {
    fetcher.load('/api/virtual-office');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch data when modal opens
  const handleOpen = () => {
    setIsOpen(true);
    // Refresh data when opening
    fetcher.load('/api/virtual-office');
  };

  // Manual refresh
  const handleRefresh = () => {
    fetcher.load('/api/virtual-office');
  };

  const teamStatus = fetcher.data?.teamStatus || [];
  const workingCount = teamStatus.filter((t) => t.status === 'working').length;
  const breakCount = teamStatus.filter((t) => t.status === 'break').length;
  const offlineCount = teamStatus.length - workingCount - breakCount;

  // Helper to get user display name
  const getDisplayName = (member: TeamMember) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName} ${member.lastName}`;
    }
    if (member.firstName) return member.firstName;
    if (member.lastName) return member.lastName;
    return member.username;
  };

  // Helper to get user initials
  const getInitials = (member: TeamMember) => {
    const firstInitial = member.firstName?.charAt(0).toUpperCase() || '';
    const lastInitial = member.lastName?.charAt(0).toUpperCase() || '';
    if (firstInitial && lastInitial) return `${firstInitial}${lastInitial}`;
    if (firstInitial) return firstInitial;
    if (lastInitial) return lastInitial;
    return member.username.charAt(0).toUpperCase();
  };

  const hasWorkersOnline = workingCount > 0 || breakCount > 0;

  return (
    <>
      <HeaderGlobalAction
        aria-label={`Virtual Office - ${hasWorkersOnline ? `${workingCount} Online` : 'Nobody Online'}`}
        tooltipAlignment="end"
        onClick={hasWorkersOnline ? handleOpen : undefined}
        className={`relative ${!hasWorkersOnline ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <Icon
          name="phosphor/building-office"
          className="w-6 h-6 text-white/70! hover:text-white!"
        />
        {hasWorkersOnline && (
          <span className="absolute top-1.5 right-1.5 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
        )}
      </HeaderGlobalAction>

      <Modal
        open={isOpen}
        onRequestClose={() => setIsOpen(false)}
        passiveModal
        modalHeading="Virtual Office"
        size="md"
      >
        <div className="pb-4">
          {/* Summary Bar with Refresh Button */}
          <div className="flex gap-6 mb-6 px-2 text-sm font-medium border-b border-gray-200 dark:border-gray-700 pb-4 items-center justify-between">
            <div className="flex gap-6">
              <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                {workingCount} Working
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
                {breakCount} On Break
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <div className="w-2.5 h-2.5 rounded-full border-2 border-gray-300 dark:border-gray-600"></div>
                {offlineCount} Offline
              </div>
            </div>

            <Button
              kind="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={fetcher.state === 'loading'}
              className="flex items-center gap-1.5 text-primary-base!"
            >
              {fetcher.state === 'loading' ? 'Refreshing...' : 'Refresh'}
              <Reset
                className={`fill-primary-base! ${fetcher.state === 'loading' ? 'animate-spin' : ''}`}
              />
            </Button>
          </div>

          {/* Loading State */}
          {fetcher.state === 'loading' && teamStatus.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Icon
                name="carbon/restart"
                size="lg"
                className="animate-spin mx-auto mb-2"
              />
              <p>Loading team status...</p>
            </div>
          )}

          {/* Empty State */}
          {fetcher.state === 'idle' && teamStatus.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No team members found</p>
            </div>
          )}

          {/* Desk Cards Grid */}
          {teamStatus.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {teamStatus
                .sort((a, b) => {
                  // Sort: self first, then by status (working > break > offline)
                  if (a.isSelf && !b.isSelf) return -1;
                  if (!a.isSelf && b.isSelf) return 1;

                  const order = { working: 1, break: 2, offline: 3 };
                  return (
                    order[a.status as keyof typeof order] -
                    order[b.status as keyof typeof order]
                  );
                })
                .map((member) => (
                  <div
                    key={member.id}
                    className={`flex flex-col rounded-lgx border transition-all overflow-hidden ${
                      member.status === 'working'
                        ? 'bg-white dark:bg-gray-800 border-green-500/40 shadow-sm'
                        : member.status === 'break'
                          ? 'bg-white dark:bg-gray-800 border-amber-400/40 dark:border-amber-400/40 shadow-sm'
                          : 'bg-gray-50/50 dark:bg-gray-900/30 border-gray-200 dark:border-gray-800 opacity-60'
                    }`}
                  >
                    {/* Top Section: Identity */}
                    <div className="flex items-start gap-4 p-4 pb-3 relative">
                      {/* Employment Type Tag */}
                      {/* {member.employmentRecord?.employmentType && (
                        <div className="absolute top-3 right-3">
                          <span className="text-[9px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600">
                            {member.employmentRecord.employmentType.replace(/_/g, ' ')}
                          </span>
                        </div>
                      )} */}

                      {/* Avatar */}
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-sm font-bold shadow-sm relative ${
                          /* State 1: You + No Photo (Coral Gradient) */
                          member.isSelf && !member.imageObjectKey
                            ? 'bg-linear-to-tr from-primary-base to-[#ff9a8b] text-white'
                            : /* State 2: You + Has Photo (The Coral Ring) */
                              member.isSelf && member.imageObjectKey
                              ? 'ring-2 ring-primary-base ring-offset-2 dark:ring-offset-gray-800'
                              : /* State 3: Teammate + Offline */
                                member.status === 'offline'
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                                : /* State 4: Teammate + Online */
                                  'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {member.imageObjectKey ? (
                          <Img
                            src={getUserImgSrc(member.imageObjectKey)}
                            alt={getDisplayName(member)}
                            className="w-full h-full object-cover rounded-full"
                            width={48}
                            height={48}
                          />
                        ) : (
                          getInitials(member)
                        )}
                      </div>

                      <div className="flex-1 min-w-0 pt-0.5">
                        <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                          {getDisplayName(member)}{' '}
                          {member.isSelf && (
                            <span className="text-xs font-normal text-gray-500 ml-1">
                              (You)
                            </span>
                          )}
                        </h4>
                        {member.employmentRecord?.position && (
                          <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {member.employmentRecord.position.title}
                            {member.employmentRecord.secondaryPositions &&
                              member.employmentRecord.secondaryPositions
                                .length > 0 &&
                              member.employmentRecord.secondaryPositions.map(
                                (secondary, idx) => (
                                  <span key={idx}>
                                    {' / '}
                                    {secondary.position.title}
                                  </span>
                                ),
                              )}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Bottom Section: Session Dashboard */}
                    <div
                      className={`px-4 py-2.5 text-xs flex justify-between items-center border-t ${
                        member.status === 'working'
                          ? 'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30'
                          : member.status === 'break'
                            ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-100 dark:border-amber-900/30'
                            : 'bg-transparent border-gray-100 dark:border-gray-800'
                      }`}
                    >
                      {/* Left Side: Status */}
                      <div className="flex items-center gap-2">
                        {member.status === 'working' && (
                          <>
                            <span className="relative flex h-2 w-2 shrink-0">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span className="font-medium text-green-700 dark:text-green-400">
                              Active
                            </span>
                            {member.sessionStart && (
                              <span className="text-gray-400 dark:text-gray-500 hidden sm:inline">
                                • Started {member.sessionStart}
                              </span>
                            )}
                          </>
                        )}
                        {member.status === 'break' && (
                          <>
                            <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0"></span>
                            <span className="font-medium text-amber-700 dark:text-amber-400">
                              On Break
                            </span>
                            {member.sessionStart && (
                              <span className="text-gray-400 dark:text-gray-500 hidden sm:inline">
                                • Since {member.sessionStart}
                              </span>
                            )}
                          </>
                        )}
                        {member.status === 'offline' && (
                          <>
                            <span className="w-2 h-2 rounded-full border border-gray-400 shrink-0"></span>
                            <span className="text-gray-500">
                              {member.isOnLeave ? 'On Leave' : 'Offline'}
                            </span>
                            {member.isOnLeave && member.leaveTypeName && (
                              <span className="text-gray-400 dark:text-gray-500 hidden sm:inline">
                                • {member.leaveTypeName}
                              </span>
                            )}
                            {!member.isOnLeave && member.lastSeen && (
                              <span className="text-gray-400 dark:text-gray-500 hidden sm:inline">
                                • {member.lastSeen}
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {/* Right Side: Total Time or Break Info */}
                      {member.status === 'break' && member.breakInfo && (
                        <div className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                          {member.breakInfo}
                        </div>
                      )}
                      {member.status === 'working' && member.totalToday && (
                        <div
                          className={`font-mono font-medium ${
                            member.workSeconds > 8 * 3600
                              ? 'text-primary-base'
                              : 'text-gray-600 dark:text-gray-300'
                          }`}
                        >
                          {member.totalToday}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
